import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { formatDistanceToNow } from "date-fns";
import { useTranslation } from "@/context/LanguageContext";
import { useRoomActivityRealtime } from "@/hooks/useRoomActivityRealtime";

export const RoomActivityFeed: React.FC<{ roomId: string }> = ({ roomId }) => {
  const { t } = useTranslation();
  const { activities, loading } = useRoomActivityRealtime(roomId);
  const [collapsed, setCollapsed] = React.useState(true);

  if (loading) return null;

  if (activities.length === 0) {
    return (
      <div className="mb-4 flex flex-col items-center justify-center rounded-xl border border-dashed border-border/60 bg-card/40 px-4 py-6 text-center">
        <p className="text-sm font-medium text-foreground">
          {t("room.empty.activity.title") || "No activity yet"}
        </p>
        <p className="mt-1 max-w-xs text-xs text-muted-foreground">
          {t("room.empty.activity.subtitle") ||
            "Add content or chat to start the room timeline."}
        </p>
      </div>
    );
  }

  const displayed = collapsed ? activities.slice(0, 3) : activities;
  const remaining = activities.length - 3;

  return (
    <div className="mb-4 rounded-xl border border-border/40 bg-card p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          Activity
        </span>
        {!collapsed && (
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            className="text-[10px] font-mono text-muted-foreground/70 hover:text-muted-foreground transition-colors"
          >
            collapse
          </button>
        )}
      </div>

      <div className="space-y-0.5">
        <AnimatePresence initial={false}>
          {displayed.map((a) => {
            const isAdd = a.action === "added";
            const timeAgo = formatDistanceToNow(new Date(a.created_at))
              .replace(" minutes", "m")
              .replace(" minute", "m")
              .replace(" hours", "h")
              .replace(" hour", "h")
              .replace(" days", "d")
              .replace(" day", "d")
              .replace("about ", "")
              .replace("less than am", "<1m");

            return (
              <motion.div
                key={a.id}
                initial={a._new ? { opacity: 0, y: -8 } : false}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="grid grid-cols-[12px_1fr_auto] gap-2 items-center rounded-md px-2 py-1.5 hover:bg-muted/30 transition-colors"
              >
                <div className="flex items-center justify-center">
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${
                      isAdd ? "bg-emerald-500" : "bg-muted-foreground/30"
                    }`}
                  />
                </div>
                <div className="text-[11px] truncate leading-tight">
                  <span className="font-medium text-foreground">
                    {a.user_name}
                  </span>
                  <span className="text-muted-foreground mx-1">{a.action}</span>
                  <span className="text-foreground/80">{a.content_title}</span>
                </div>
                <span className="text-[10px] font-mono text-muted-foreground/70 whitespace-nowrap">
                  {timeAgo}
                </span>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {collapsed && remaining > 0 && (
        <div className="mt-1.5 flex justify-center border-t border-border/30 pt-1.5">
          <button
            type="button"
            onClick={() => setCollapsed(false)}
            className="text-[10px] font-mono text-muted-foreground/70 hover:text-muted-foreground transition-colors"
          >
            +{remaining} more
          </button>
        </div>
      )}
    </div>
  );
};
